import { useQuery } from "@tanstack/react-query";
import { computeStandings } from "@alpha/engine";
import type { EngineTeam, EngineGame, EngineConfig } from "@alpha/engine";
import { isPubliclyVisible } from "@alpha/shared";
import { api } from "../lib/api";
import type { TournamentListItem } from "../lib/types";
import { formatPct } from "../lib/format";
import { useBranding } from "../lib/siteSettings";
import { applyFilters, DEFAULT_FILTERS, sortTournaments } from "../lib/tournamentSearch";
import { TournamentCard } from "../components/TournamentCard";
import {
  Badge,
  ButtonLink,
  Card,
  DiamondMark,
  EmptyState,
  Section,
  Skeleton,
  stagger,
  TrophyIcon,
  cx,
} from "../components/ui";

const demoConfig: EngineConfig = {
  runCapEnabled: true,
  runCapMargin: 8,
  forfeitScore: { winner: 7, loser: 0 },
  gameLengthMinutes: 90,
  minRestMinutes: 30,
};

const demoTeams: EngineTeam[] = [
  { id: "hooks", name: "Hooks", poolId: "A" },
  { id: "raptors", name: "Raptors", poolId: "A" },
  { id: "bandits", name: "River Bandits", poolId: "A" },
];

const demoGames: EngineGame[] = [
  {
    id: "g1",
    type: "pool",
    poolId: "A",
    homeTeamId: "hooks",
    awayTeamId: "raptors",
    status: "final",
    homeScore: 6,
    awayScore: 2,
  },
  {
    id: "g2",
    type: "pool",
    poolId: "A",
    homeTeamId: "raptors",
    awayTeamId: "bandits",
    status: "final",
    homeScore: 3,
    awayScore: 3,
  },
  {
    id: "g3",
    type: "pool",
    poolId: "A",
    homeTeamId: "bandits",
    awayTeamId: "hooks",
    status: "final",
    homeScore: 1,
    awayScore: 4,
  },
];

/** Public landing: hero, published tournaments, and the in-browser engine demo. */
export function PublicHome() {
  const [pool] = computeStandings(demoTeams, demoGames, demoConfig);
  // The hero speaks in the organization's own voice: an admin's logo, name, and
  // tagline headline the page, same set the nav and footer already carry.
  const branding = useBranding();

  const tournamentsQ = useQuery({
    queryKey: ["public-tournaments"],
    queryFn: () => api.get<{ tournaments: TournamentListItem[] }>("/public/tournaments"),
    staleTime: 60_000,
    retry: 1,
  });
  const events = (tournamentsQ.data?.tournaments ?? []).filter((t) => isPubliclyVisible(t.status));
  // The four soonest upcoming events — a taste of what's on, with the full
  // search, filters, and past events over on /tournaments. Four rather than
  // three because the grid below runs four across on a wide screen, and a row
  // with a hole in it reads as a page that failed to load its last card.
  const upcoming = sortTournaments(applyFilters(events, DEFAULT_FILTERS), "date").slice(0, 4);

  return (
    <div className="space-y-10 sm:space-y-14">
      {/* Hero */}
      <section className="relative animate-rise overflow-hidden rounded-3xl bg-navy-950 px-6 py-14 text-white sm:px-12 sm:py-20">
        <div className="pointer-events-none absolute -top-24 right-0 h-72 w-72 rounded-full bg-brand-500/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-brand-400/10 blur-3xl" />
        <svg
          className="pointer-events-none absolute -right-16 top-1/2 hidden h-[420px] w-[420px] -translate-y-1/2 text-white/[.07] md:block"
          viewBox="0 0 100 100"
          fill="none"
          aria-hidden
        >
          <path d="M50 6 94 50 50 94 6 50Z" stroke="currentColor" strokeWidth="1.5" />
          <path d="M50 22 78 50 50 78 22 50Z" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="50" cy="50" r="3" fill="currentColor" />
        </svg>
        <div className="relative max-w-2xl space-y-5">
          <Badge tone="brand" className="bg-white/10 text-brand-200 ring-white/15">
            Live standings · Schedules · Brackets
          </Badge>
          {/*
            The mark and the name are one lockup, the way a crest and a wordmark
            sit together on a jersey — the eyebrow above is about the product,
            these two are the organization. An uploaded logo sits on a plate in
            the app's own blue — the same fill DiamondMark gives its square, so
            the branded and unbranded states read as one mark rather than two
            unrelated objects. The plate is sized to the logo rather than square:
            most youth programs have a wordmark, far wider than it is tall.
          */}
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-5">
            {branding.logoUrl ? (
              // The plate is inline-block, not flex: the logo takes its height
              // from the plate and its width from its own proportions, which a
              // flex item cannot do — there the width has nothing to resolve
              // against and the plate collapses to a sliver. `object-contain`
              // then letterboxes anything past the cap rather than stretching it.
              // The white ring earns its keep now that the plate is no longer
              // light: it is the hairline holding the blue plate off the navy
              // behind it, which now shares its hue family.
              <span className="inline-block h-16 shrink-0 rounded-2xl bg-brand-600 p-2.5 shadow-lg shadow-navy-950/40 ring-1 ring-white/25 sm:h-20 sm:p-3">
                <img
                  src={branding.logoUrl}
                  alt=""
                  className="block h-full w-auto max-w-56 object-contain"
                />
              </span>
            ) : (
              <DiamondMark className="h-16 w-16 shrink-0 sm:h-20 sm:w-20" />
            )}
            {/* An org name runs to 80 characters and may be a single word, so it
                wraps on its own rather than being split onto fixed lines. */}
            <h1 className="min-w-0 font-display text-balance text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl">
              {branding.orgName}
            </h1>
          </div>
          {/* The tagline is optional — an admin who clears it gets no empty gap. */}
          {branding.tagline ? (
            <p className="max-w-xl text-base text-slate-300 sm:text-lg">{branding.tagline}</p>
          ) : null}
          <div className="flex flex-wrap gap-3 pt-1">
            <ButtonLink to="/me/register" size="lg">
              Register a team
            </ButtonLink>
            <ButtonLink variant="onDark" size="lg" to="/tournaments">
              Browse tournaments
            </ButtonLink>
          </div>
        </div>
      </section>

      {/* Published tournaments */}
      <Section
        title="Upcoming tournaments"
        actions={
          <ButtonLink variant="secondary" size="sm" to="/tournaments">
            Browse all
          </ButtonLink>
        }
        className="animate-rise"
        style={stagger(2)}
      >
        {tournamentsQ.isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-48 rounded-2xl" />
            ))}
          </div>
        ) : tournamentsQ.isError ? (
          <EmptyState
            title="Tournament listings are unavailable right now"
            hint="Please check back shortly."
          />
        ) : upcoming.length === 0 ? (
          <EmptyState
            icon={<TrophyIcon className="h-5 w-5" />}
            title="No upcoming tournaments"
            hint={
              events.length > 0
                ? "Nothing is scheduled ahead, but there are past events to browse."
                : "New events will show up here as soon as organizers publish them."
            }
            action={
              // Carry the timeframe in the URL: /tournaments opens on upcoming,
              // which is empty in exactly the case that renders this button.
              events.length > 0 ? (
                <ButtonLink variant="secondary" to="/tournaments?when=past">
                  Browse past tournaments
                </ButtonLink>
              ) : undefined
            }
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {upcoming.map((t, i) => (
              <div key={t.id} className="min-w-0 animate-rise" style={stagger(i)}>
                <TournamentCard t={t} terms={[]} />
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Engine demo scoreboard */}
      <div className="animate-rise" style={stagger(4)}>
        <Card className="overflow-hidden lg:grid lg:grid-cols-2">
          <div className="space-y-4 bg-navy-950 p-6 text-white sm:p-10">
            <Badge tone="brand" pulse className="bg-white/10 text-brand-200 ring-white/15">
              Live demo
            </Badge>
            <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
              Standings, computed in your browser
            </h2>
            <p className="text-sm leading-relaxed text-slate-300 sm:text-base">
              The same pure engine powers every schedule, seeding, and bracket on this site —
              results update the moment a score goes final, with no page refresh.
            </p>
          </div>
          <div className="p-4 sm:p-6">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  <th className="px-3 py-2.5">#</th>
                  <th className="py-2.5 pr-3">Team</th>
                  <th className="px-2 py-2.5 text-center">W</th>
                  <th className="px-2 py-2.5 text-center">L</th>
                  <th className="px-2 py-2.5 text-center">T</th>
                  <th className="px-3 py-2.5 text-right">Pct</th>
                </tr>
              </thead>
              <tbody>
                {pool?.rows.map((r, i) => (
                  <tr
                    key={r.teamId}
                    className="animate-rise border-b border-slate-50 transition-colors last:border-0 hover:bg-brand-50/40"
                    style={stagger(i + 2)}
                  >
                    <td className="px-3 py-2.5">
                      <span
                        className={cx(
                          "flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold tabular-nums",
                          i === 0
                            ? "bg-brand-600 text-brandFg shadow-sm shadow-brand-600/30"
                            : "bg-slate-100 text-slate-500",
                        )}
                      >
                        {i + 1}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3 font-medium text-navy-950">{r.teamName}</td>
                    <td className="px-2 text-center font-semibold tabular-nums text-navy-950">
                      {r.wins}
                    </td>
                    <td className="px-2 text-center tabular-nums text-slate-500">{r.losses}</td>
                    <td className="px-2 text-center tabular-nums text-slate-500">{r.ties}</td>
                    <td className="px-3 text-right tabular-nums text-slate-600">
                      {formatPct(r.winPct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
